import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

export function useNotifications() {
  const { api, user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [permissionState, setPermissionState] = useState("default");
  const [connected, setConnected] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const eventSourceRef = useRef(null);
  const audioRef = useRef(null);

  // Initialize audio
  useEffect(() => {
    audioRef.current = {
      play: () => {
        try {
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          [660, 880].forEach((freq, i) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.frequency.value = freq;
            osc.type = "sine";
            gain.gain.setValueAtTime(0.15, ctx.currentTime + i * 0.15);
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.15 + 0.3);
            osc.start(ctx.currentTime + i * 0.15);
            osc.stop(ctx.currentTime + i * 0.15 + 0.3);
          });
        } catch {}
      },
    };
  }, []);

  // Check notification permission
  useEffect(() => {
    if ("Notification" in window) {
      setPermissionState(Notification.permission);
    }
  }, []);

  // Check if already push-subscribed
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready.then((reg) => {
        reg.pushManager.getSubscription().then((sub) => {
          setPushSubscribed(!!sub);
        });
      }).catch(() => {});
    }
  }, []);

  const requestPermission = useCallback(async () => {
    if (!("Notification" in window)) return "denied";
    const result = await Notification.requestPermission();
    setPermissionState(result);
    return result;
  }, []);

  // Subscribe to Web Push
  const subscribePush = useCallback(async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !api) return false;

    try {
      // Request permission first
      const perm = await requestPermission();
      if (perm !== "granted") return false;

      const reg = await navigator.serviceWorker.ready;
      const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      if (!vapidKey) {
        console.warn("VAPID public key not configured");
        return false;
      }

      let subscription = await reg.pushManager.getSubscription();

      if (!subscription) {
        subscription = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });
      }

      // Send subscription to backend
      const subJson = subscription.toJSON();
      await api.post("/tenant/push/subscribe", {
        endpoint: subJson.endpoint,
        keys: subJson.keys,
      });

      setPushSubscribed(true);
      return true;
    } catch (err) {
      console.error("Push subscribe error:", err);
      return false;
    }
  }, [api, requestPermission]);

  // Unsubscribe from Web Push
  const unsubscribePush = useCallback(async () => {
    if (!("serviceWorker" in navigator) || !api) return;

    try {
      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.getSubscription();
      if (subscription) {
        await api.delete("/tenant/push/unsubscribe", {
          data: { endpoint: subscription.endpoint },
        });
        await subscription.unsubscribe();
      }
      setPushSubscribed(false);
    } catch (err) {
      console.error("Push unsubscribe error:", err);
    }
  }, [api]);

  // Show native notification
  const showNativeNotification = useCallback((title, body, data = {}) => {
    if (!("Notification" in window) || Notification.permission !== "granted") return;
    try {
      const n = new Notification(title, {
        body,
        icon: "/icons/icon-192x192.png",
        badge: "/icons/icon-96x96.png",
        tag: data.order_number || "order",
        renotify: true,
        requireInteraction: true,
        data,
      });
      n.onclick = () => {
        window.focus();
        n.close();
      };
    } catch {
      if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: "SHOW_NOTIFICATION",
          title,
          body,
          data,
        });
      }
    }
  }, []);

  // Connect to SSE stream
  useEffect(() => {
    if (!user || !api) return;

    const baseUrl = api.defaults.baseURL || "";
    const url = `${baseUrl}/tenant/notifications/stream`;
    const authHeader = api.defaults.headers?.Authorization;

    let retryTimeout;
    let retryCount = 0;

    const connect = () => {
      const controller = new AbortController();
      eventSourceRef.current = controller;

      fetch(url, {
        credentials: "include",
        headers: authHeader ? { Authorization: authHeader } : {},
        signal: controller.signal,
      })
        .then((response) => {
          if (!response.ok) {
            setConnected(false);
            if (response.status === 404 || response.status === 401) return; // Don't retry on 404/401
            scheduleRetry();
            return;
          }
          setConnected(true);
          retryCount = 0;

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";

          const processStream = () => {
            reader
              .read()
              .then(({ done, value }) => {
                if (done) {
                  setConnected(false);
                  scheduleRetry();
                  return;
                }
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split("\n");
                buffer = lines.pop() || "";

                for (const line of lines) {
                  if (line.startsWith("data: ")) {
                    try {
                      const event = JSON.parse(line.slice(6));
                      if (event.type === "new_order") {
                        const d = event.data;
                        const notification = {
                          id: Date.now(),
                          type: "new_order",
                          title: `New Order #${d.order_number}`,
                          body: `${d.customer_name} — $${d.total?.toFixed(2)} (${d.items_count} items)`,
                          data: d,
                          timestamp: event.timestamp,
                          read: false,
                        };
                        setNotifications((prev) => [notification, ...prev].slice(0, 50));
                        setUnreadCount((c) => c + 1);
                        audioRef.current?.play();
                        showNativeNotification(notification.title, notification.body, d);
                      }
                    } catch {}
                  }
                }
                processStream();
              })
              .catch(() => {
                setConnected(false);
                scheduleRetry();
              });
          };
          processStream();
        })
        .catch(() => {
          setConnected(false);
          scheduleRetry();
        });
    };

    const scheduleRetry = () => {
      retryCount++;
      // Never give up permanently — back off up to 60s and keep trying
      const delay = Math.min(2000 * Math.pow(2, Math.min(retryCount, 5)), 60000);
      retryTimeout = setTimeout(connect, delay);
    };

    connect();

    return () => {
      if (eventSourceRef.current) eventSourceRef.current.abort();
      clearTimeout(retryTimeout);
    };
  }, [user, api, showNativeNotification]);

  const markAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  return {
    notifications,
    unreadCount,
    connected,
    permissionState,
    pushSubscribed,
    requestPermission,
    subscribePush,
    unsubscribePush,
    markAllRead,
    clearAll,
  };
}
